import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Keyboard } from 'lucide-react';
import { SHORTCUTS } from '../../hooks/useKeyboardShortcuts';

export default function KeyboardShortcutsModal() {
  const [open, setOpen] = useState(false);
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  useEffect(() => {
    const handleShowShortcuts = () => setOpen(true);
    window.addEventListener('show-shortcuts-help', handleShowShortcuts);
    return () => window.removeEventListener('show-shortcuts-help', handleShowShortcuts);
  }, []);

  const renderKeys = (keys, macKeys) => {
    const keysToRender = isMac && macKeys ? macKeys : keys;
    return (
      <div className="flex gap-1">
        {keysToRender.map((key, idx) => (
          <Badge key={idx} variant="outline" className="font-mono text-xs">
            {key}
          </Badge>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Navigation Shortcuts */}
          <div>
            <h3 className="font-semibold mb-3">Navigation</h3>
            <div className="space-y-2">
              {SHORTCUTS.navigation.map((shortcut, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <span className="text-sm">{shortcut.action}</span>
                  {renderKeys(shortcut.keys, shortcut.mac)}
                </div>
              ))}
            </div>
          </div>

          {/* Action Shortcuts */}
          <div>
            <h3 className="font-semibold mb-3">Actions</h3>
            <div className="space-y-2">
              {SHORTCUTS.actions.map((shortcut, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <span className="text-sm">{shortcut.action}</span>
                  {renderKeys(shortcut.keys, shortcut.mac)}
                </div>
              ))}
            </div>
          </div>

          {/* Help Shortcuts */}
          <div>
            <h3 className="font-semibold mb-3">Help</h3>
            <div className="space-y-2">
              {SHORTCUTS.help.map((shortcut, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <span className="text-sm">{shortcut.action}</span>
                  {renderKeys(shortcut.keys, shortcut.mac)}
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-muted-foreground mt-4 p-3 bg-muted rounded">
            <p><strong>Tip:</strong> Shortcuts work when you're not typing in a form field. Press <Badge variant="outline" className="font-mono mx-1">?</Badge> anytime to see this help.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
